import React, { FC, useRef, useState } from 'react';

import { Box, Flex } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import Theme from '@adsk/alloy-react-theme';

import {
  PRICING_URL,
  AUTOSPECS_TRIAL_MARKETING_URL,
  OFFERING_NAMES,
} from './helpers/const';
import { SUBSCRIPTION_STATUS } from '../../helpers/const';
import { Subscription, SubscriptionConstruction } from '../../helpers/types';
import { handleCheckoutFlow } from '../../helpers/utils';
import { useTranslation } from '../../i18n';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';
import Banner from '@adsk/alloy-react-banner';
import TrialModalOverlay from '../TrialModalOverlay';

const buttonStyle = {
  ...Theme.typography.labelSmall,
  height: '24px',
  minWidth: '134px',
  padding: '0 8px',
};

interface TrialModalItemProps {
  userTrial: Subscription | SubscriptionConstruction;
  sdkInitialized?: boolean;
}

const TrialModalItem: FC<TrialModalItemProps> = ({
  userTrial,
  sdkInitialized = false,
}) => {
  const { t } = useTranslation();
  const { daysRemaining, status } = userTrial;
  const [errorMessage, setErrorMessage] = useState('');
  const [isCheckoutFlowSuccessful, setIsCheckoutFlowSuccessful] =
    useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const popupControlsRef = useRef<{
    focus: () => void;
    close: () => void;
  } | null>(null);
  const offeringCategoryOrName =
    'offeringCategoryName' in userTrial
      ? userTrial.offeringCategoryName
      : userTrial.offeringName;
  const offeringCategory =
    'offeringCategory' in userTrial ? userTrial.offeringCategory : ''; // empty string means the pricing home page
  const nameContainsAutoSpecs = userTrial.offeringName.includes(
    OFFERING_NAMES.AUTOSPECS
  );

  const daysLeftText =
    daysRemaining > 1
      ? t('MODAL_ITEM_TEXT_EXPIRED_COUNT_MULTIPLE', { daysRemaining })
      : t('MODAL_ITEM_TEXT_EXPIRED_COUNT_SINGLE', { daysRemaining });

  const { value: useCheckoutFlowFlag, loading: loadingCheckoutFlowFlag } =
    useAlloyFeatureFlag('ACSPE_CHECKOUT_FLOW', false);

  const { locale } = useLocale();

  const startCheckoutFlow = () => {
    handleCheckoutFlow({
      trial: userTrial,
      language: locale ?? 'en',
      onError: (error: string) => {
        setShowOverlay(false);
        setErrorMessage(t(error));
      },
      onComplete: () => {
        setShowOverlay(false);
        setIsCheckoutFlowSuccessful(true);
      },
      onCancel: () => {
        setShowOverlay(false);
      },
      onPopupInit: (controls) => {
        popupControlsRef.current = controls;
      },
    });
  };

  const handleRelaunch = () => {
    popupControlsRef.current?.focus();
  };

  const handleCloseOverlay = () => {
    popupControlsRef.current?.close();
    setShowOverlay(false);
  };

  const handleClickBuyingOptions = () => {
    if (useCheckoutFlowFlag && !loadingCheckoutFlowFlag && sdkInitialized) {
      setShowOverlay(true);
      startCheckoutFlow();
    } else {
      const buyingOptionsLink = nameContainsAutoSpecs
        ? AUTOSPECS_TRIAL_MARKETING_URL
        : `${PRICING_URL}/${offeringCategory}`;
      window.open(buyingOptionsLink, '_blank');
    }
  };

  return (
    <>
      <Flex
        paddingBottom="16px"
        flexDirection="row"
        justifyContent="space-between"
      >
        <Box>
          <Box typography="bodyMedium">{offeringCategoryOrName}</Box>
          <Box
            typography="bodyMedium"
            style={{
              color: isCheckoutFlowSuccessful
                ? Theme.colors.green500
                : daysRemaining !== null && daysRemaining < 3
                  ? Theme.colors.red500
                  : Theme.colors.charcoal900,
            }}
          >
            {isCheckoutFlowSuccessful
              ? t('PURCHASED_ON_DATE', {
                  date: new Date().toLocaleDateString(locale ?? 'en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                })
              : status === SUBSCRIPTION_STATUS.ACTIVE
                ? daysLeftText
                : t('MODAL_ITEM_TEXT_EXPIRED')}
          </Box>
        </Box>
        <Button
          data-testid="TrialModalItem_button"
          as="a"
          target="_blank"
          onClick={handleClickBuyingOptions}
          style={buttonStyle}
          variant={Button.VARIANTS.PRIMARY}
          disabled={isCheckoutFlowSuccessful}
        >
          {t('MODAL_ITEM_TEXT_BUTTON_TEXT')}
        </Button>
      </Flex>
      {errorMessage && (
        <Banner
          data-testid="TrialModalItem_error_message_banner"
          message={errorMessage}
          variant={Banner.VARIANTS.WARNING}
          style={{ position: 'relative', marginBottom: '20px' }}
          onClose={() => {
            setErrorMessage('');
          }}
        />
      )}
      <TrialModalOverlay
        show={showOverlay}
        onClose={handleCloseOverlay}
        onRelaunch={handleRelaunch}
      />
    </>
  );
};

TrialModalItem.displayName = 'TrialModalItem';

export default TrialModalItem;
